import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Video, MapPin, Users, FileText, MessageSquare,
  Play, Pause, Square, Loader2, ClipboardList, PenLine
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AttendancePanel, MeetingClock, AgendaItemRow, ParkingLotPanel, ResizableMeetingLayout, TimeAnalyticsPanel } from './meeting';
import MeetingQuickLinks from './meeting/MeetingQuickLinks';

const MAX_DOC_TABS = 5;

const normalizeDocumentForTab = (doc) => ({
  document_id: doc.document_id || doc.id,
  title: doc.title || doc.name || 'Document',
  file_type: doc.file_type || doc.mime_type || '',
  file_url: doc.file_url || doc.url || null,
  preview_url: doc.preview_url || doc.pdf_preview_url || null
});

const isPdfType = (fileType) => fileType?.includes('pdf');
const isImageType = (fileType) => fileType?.includes('image');
const withPdfZoom = (url) => {
  if (!url) return url;
  if (url.includes('zoom=')) return url;
  const params = 'zoom=90&view=FitH&pagemode=none&navpanes=0';
  return url.includes('#') ? `${url}&${params}` : `${url}#${params}`;
};


const CommitteeMeetingRunner = () => {
  const { committeeId, meetingId } = useParams();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [timing, setTiming] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [meetingDocs, setMeetingDocs] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showDocumentPicker, setShowDocumentPicker] = useState(null);
  const [docTabs, setDocTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState('agenda');
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [noteTakingMode, setNoteTakingMode] = useState(false);
  const [agendaNotes, setAgendaNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [notesReadOnly, setNotesReadOnly] = useState(false);
  const [hasNotesLock, setHasNotesLock] = useState(false);
  const [canGenerateFollowupTasks, setCanGenerateFollowupTasks] = useState(false);
  const [generatingFollowupTasks, setGeneratingFollowupTasks] = useState(false);
  const [showTimeAnalytics, setShowTimeAnalytics] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [showParkingLot, setShowParkingLot] = useState(false);

  const fetchLiveState = useCallback(async () => {
    try {
      const response = await api.get(`/governance/committees/${committeeId}/meetings/${meetingId}/live-state`);
      setMeeting(response.data.meeting);
      setAgendaItems(response.data.agenda_items || []);
      setTiming(response.data.timing || {});
      setError(null);
    } catch (err) {
      console.error('Failed to fetch committee live state:', err);
      setError('Failed to load meeting');
    } finally {
      setLoading(false);
    }
  }, [committeeId, meetingId]);

  const fetchMeetingDocuments = useCallback(async (query = '') => {
    try {
      setDocumentsLoading(true);
      const params = new URLSearchParams();
      if (query) {
        params.append('q', query);
      }
      const res = await api.get(`/governance/committees/${committeeId}/meetings/${meetingId}/documents?${params.toString()}`);
      setMeetingDocs(res.data.linked_documents || []);
      setAvailableDocuments(res.data.available_documents || []);
    } catch (err) {
      console.error('Failed to fetch committee meeting documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [committeeId, meetingId]);

  const fetchCommitteeMembers = useCallback(async () => {
    try {
      const res = await api.get(`/governance/committees/${committeeId}/members`);
      setCommitteeMembers(res.data.members || []);
    } catch (err) {
      console.error('Failed to fetch committee members:', err);
    }
  }, [committeeId]);

  const fetchEditStatus = useCallback(async () => {
    try {
      const res = await api.get(`/governance/committees/${committeeId}/meetings/${meetingId}/minutes/edit-status`);
      setCanGenerateFollowupTasks(!!res.data?.can_generate_followup_tasks);
    } catch (err) {
      console.error('Failed to fetch committee minutes edit status:', err);
    }
  }, [committeeId, meetingId]);

  useEffect(() => {
    fetchLiveState();
    fetchMeetingDocuments();
    fetchCommitteeMembers();
    fetchEditStatus();
    const interval = setInterval(fetchLiveState, 3000);
    return () => clearInterval(interval);
  }, [fetchLiveState, fetchMeetingDocuments, fetchCommitteeMembers, fetchEditStatus]);

  useEffect(() => {
    setDocTabs([]);
    setActiveTabId('agenda');
  }, [meetingId]);

  useEffect(() => {
    if (showDocumentPicker) {
      setDocumentSearch('');
    }
  }, [showDocumentPicker]);

  useEffect(() => {
    if (!showDocumentPicker) return;
    const term = documentSearch.trim();
    const timeout = setTimeout(() => {
      fetchMeetingDocuments(term);
    }, 200);
    return () => clearTimeout(timeout);
  }, [showDocumentPicker, documentSearch, fetchMeetingDocuments]);

  const linkedDocsByAgenda = useMemo(() => {
    const grouped = {};
    meetingDocs.forEach((doc) => {
      const key = doc.agenda_item_id || 'general';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });
    return grouped;
  }, [meetingDocs]);

  const openDocumentTab = useCallback((doc) => {
    if (!doc) return;
    const normalized = normalizeDocumentForTab(doc);
    setDocTabs((prev) => {
      const exists = prev.find((t) => t.document_id === normalized.document_id);
      if (exists) return prev;
      const next = prev.length >= MAX_DOC_TABS ? [...prev.slice(1), normalized] : [...prev, normalized];
      return next;
    });
    setActiveTabId(normalized.document_id);
  }, []);

  const closeDocTab = (docId) => {
    setDocTabs((prev) => {
      const next = prev.filter((t) => t.document_id !== docId);
      if (activeTabId === docId) {
        setActiveTabId(next.length > 0 ? next[next.length - 1].document_id : 'agenda');
      }
      return next;
    });
  };

  const resolveDocumentById = useCallback(async (documentId) => {
    const fromMeeting = meetingDocs.find((d) => d.document_id === documentId || d.id === documentId);
    if (fromMeeting) {
      return normalizeDocumentForTab(fromMeeting);
    }
    try {
      const res = await api.get(`/board/documents/${documentId}`);
      return normalizeDocumentForTab(res.data);
    } catch (err) {
      console.error('Failed to fetch document details:', err);
      return null;
    }
  }, [meetingDocs]);

  const handleOpenDocumentById = useCallback(async (documentId, titleOverride) => {
    const doc = await resolveDocumentById(documentId);
    if (!doc) {
      toast.error('Failed to open document');
      return;
    }
    if (titleOverride && !doc.title) {
      doc.title = titleOverride;
    }
    openDocumentTab(doc);
  }, [openDocumentTab, resolveDocumentById]);

  const handleOpenFirstMeetingDoc = useCallback(() => {
    if (!meetingDocs.length) {
      toast.error('No documents linked to this meeting');
      return;
    }
    openDocumentTab(meetingDocs[0]);
  }, [meetingDocs, openDocumentTab]);

  const handleLinkDocument = async (documentId, agendaItemId) => {
    try {
      await api.post(`/governance/committees/${committeeId}/meetings/${meetingId}/documents`, {
        document_id: documentId,
        agenda_item_id: agendaItemId
      });
      toast.success('Document linked to agenda item');
      setShowDocumentPicker(null);
      fetchMeetingDocuments();
    } catch (err) {
      console.error('Failed to link document:', err);
      toast.error('Failed to link document');
    }
  };

  const handleMeetingAction = async (action) => {
    if (!committeeId || !meetingId) return;
    setActionLoading(true);
    try {
      await api.post(`/governance/committees/${committeeId}/meetings/${meetingId}/${action}`);
      await fetchLiveState();
    } catch (err) {
      console.error(`Failed to ${action} meeting:`, err);
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAgendaAction = async (itemId, action) => {
    if (!committeeId || !meetingId || !itemId) return;
    setActionLoading(true);
    try {
      await api.post(`/governance/committees/${committeeId}/meetings/${meetingId}/agenda/${itemId}/${action}`);
      await fetchLiveState();
    } catch (err) {
      console.error(`Failed to ${action} agenda item:`, err);
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const releaseNotesLock = useCallback(async () => {
    if (!hasNotesLock) return;
    try {
      await api.delete(`/governance/committees/${committeeId}/meetings/${meetingId}/minutes/lock`);
    } catch (err) {
      console.warn('Failed to release committee notes lock:', err);
    } finally {
      setHasNotesLock(false);
    }
  }, [committeeId, meetingId, hasNotesLock]);

  const acquireNotesLock = async () => {
    try {
      await api.post(`/governance/committees/${committeeId}/meetings/${meetingId}/minutes/lock`);
      setHasNotesLock(true);
      setNotesReadOnly(false);
      return true;
    } catch (err) {
      const message = err.response?.data?.detail || 'Notes are locked by another user';
      if (err.response?.status === 423) {
        toast.warning(message);
      } else {
        toast.error(message);
      }
      setNotesReadOnly(true);
      setHasNotesLock(false);
      return false;
    }
  };

  const enableNoteTakingMode = async () => {
    const lockAcquired = await acquireNotesLock();
    setNoteTakingMode(true);
    setNotesReadOnly(!lockAcquired);
    try {
      const response = await api.get(`/governance/committees/${committeeId}/meetings/${meetingId}/minutes`);
      const notesMap = {};
      response.data.agenda_notes?.forEach(note => {
        notesMap[note.agenda_item_id] = note.notes || '';
      });
      setAgendaNotes(notesMap);
    } catch (err) {
      console.error('Failed to fetch committee notes:', err);
    }
  };

  const handleNotesChange = (itemId, notes) => {
    setAgendaNotes(prev => ({ ...prev, [itemId]: notes }));
  };

  const saveAgendaItemNotes = async (itemId, notes) => {
    if (notesReadOnly) {
      toast.warning('Notes are locked by another user');
      return;
    }
    setSavingNotes(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.put(`/governance/committees/${committeeId}/meetings/${meetingId}/agenda/${itemId}/notes`, {
        notes: notes
      });
      toast.success('Notes saved');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to save notes';
      toast.error(errorMessage);
    } finally {
      setSavingNotes(prev => ({ ...prev, [itemId]: false }));
    }
  };

  useEffect(() => {
    return () => {
      releaseNotesLock();
    };
  }, [releaseNotesLock]);

  const generateFollowupTasks = async () => {
    if (generatingFollowupTasks) return;
    setGeneratingFollowupTasks(true);
    try {
      const res = await api.post(`/governance/committees/${committeeId}/meetings/${meetingId}/generate-followup-tasks`);
      const { created = 0, updated = 0, skipped = 0, orphaned = 0 } = res.data || {};
      toast.success(`Follow-up tasks generated: ${created} created, ${updated} updated`);
      if (skipped || orphaned) {
        toast.message(`Skipped: ${skipped} · Orphaned: ${orphaned}`);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to generate follow-up tasks';
      toast.error(errorMessage);
    } finally {
      setGeneratingFollowupTasks(false);
    }
  };

  const goBack = () => {
    navigate(`/governance/committees/${committeeId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{error || 'Meeting not found'}</p>
        <Button onClick={goBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Committee
        </Button>
      </div>
    );
  }

  const meetingDate = new Date(meeting.scheduled_start);
  const endDate = meeting.scheduled_end ? new Date(meeting.scheduled_end) : null;

  const formatDate = (date) => date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const formatTime = (date) => date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const getStatusBadge = () => {
    if (meeting.status === 'cancelled') return <Badge variant="destructive">Cancelled</Badge>;
    if (meeting.status === 'completed') return <Badge variant="secondary">Completed</Badge>;
    if (meeting.status === 'in_progress') return <Badge className="bg-green-500 text-white">In Progress</Badge>;
    return <Badge className="bg-purple-500 text-white">Scheduled</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="committee-meeting-runner">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={goBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold text-gray-900">{meeting.title}</h1>
                  {getStatusBadge()}
                </div>
                <p className="text-sm text-gray-500">
                  {formatDate(meetingDate)} at {formatTime(meetingDate)}
                </p>
              </div>
            </div>

            <MeetingClock
              serverElapsed={timing.meeting_elapsed_seconds || 0}
              isLive={meeting.status === 'in_progress'}
              isPaused={meeting.is_paused}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleMeetingAction('start')}
              disabled={actionLoading || meeting.status === 'in_progress' || meeting.status === 'completed'}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
            >
              <Play className="h-4 w-4 mr-2" />
              Start
            </Button>
            <Button
              variant="outline"
              onClick={() => handleMeetingAction('pause')}
              disabled={actionLoading || meeting.status !== 'in_progress' || meeting.is_paused}
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
            <Button
              variant="outline"
              onClick={() => handleMeetingAction('resume')}
              disabled={actionLoading || meeting.status !== 'in_progress' || !meeting.is_paused}
            >
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
            <Button
              variant="outline"
              onClick={() => handleMeetingAction('end')}
              disabled={actionLoading || meeting.status === 'completed'}
            >
              <Square className="h-4 w-4 mr-2" />
              End
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full px-6 py-6">
        <ResizableMeetingLayout
          showLeft={true}
          defaultWidths={{ left: 16, center: 68, right: 16 }}
          defaultLeftCollapsed={false}
          maxSideWidthPx={360}
          minRightWidthPercent={12}
          leftContent={
            <div className="pr-4 h-full">
              <MeetingQuickLinks
                className="h-full"
                settingsEndpoint={`/governance/committees/${committeeId}/dashboard-settings/default`}
                settingsLink={`/governance/committees/${committeeId}/settings`}
                onOpenDocument={handleOpenDocumentById}
              />
            </div>
          }
          centerContent={
            <div className="px-3 space-y-6">
              {/* Meeting Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Meeting Details</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{formatDate(meetingDate)}</p>
                      <p className="text-sm text-gray-500">
                        {formatTime(meetingDate)}
                        {endDate && ` - ${formatTime(endDate)}`}
                      </p>
                    </div>
                  </div>

                  {meeting.location && (
                    <div className="flex items-start gap-3">
                      {meeting.location_type === 'virtual' ? (
                        <Video className="h-5 w-5 text-gray-400 mt-0.5" />
                      ) : (
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{meeting.location}</p>
                        {meeting.video_link && (
                          <a
                            href={meeting.video_link}
                            className="text-sm text-purple-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Join video call
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {meeting.description && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-gray-600">{meeting.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Agenda / Document Tabs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="flex items-center border-b border-gray-100 bg-white">
                  <button
                    className={`px-4 py-3 text-sm font-medium border-b-2 ${
                      activeTabId === 'agenda'
                        ? 'border-purple-600 text-purple-700'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setActiveTabId('agenda')}
                  >
                    Agenda
                  </button>
                  {docTabs.map((doc) => (
                    <button
                      key={doc.document_id}
                      className={`px-3 py-3 text-sm font-medium border-b-2 max-w-[200px] flex items-center gap-2 ${
                        activeTabId === doc.document_id
                          ? 'border-purple-600 text-purple-700'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => setActiveTabId(doc.document_id)}
                      title={doc.title}
                    >
                      <span className="truncate">{doc.title}</span>
                      <span
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeDocTab(doc.document_id);
                        }}
                      >
                        ×
                      </span>
                    </button>
                  ))}
                </div>

                {activeTabId === 'agenda' ? (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-purple-600" />
                        Agenda
                      </h2>
                    </div>

                    {agendaItems.length > 0 ? (
                      <div className="space-y-3">
                        {[...agendaItems]
                          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                          .map((item, index) => (
                          <AgendaItemRow
                            key={item.id}
                            item={item}
                            index={index}
                            isActive={item.is_active}
                            isChair={true}
                            onActivate={() => handleAgendaAction(item.id, 'activate')}
                            onComplete={() => handleAgendaAction(item.id, 'complete')}
                            onSkip={() => handleAgendaAction(item.id, 'skip')}
                            onDefer={() => handleAgendaAction(item.id, 'defer')}
                            serverElapsed={timing.current_item_elapsed_seconds || 0}
                            noteTakingMode={noteTakingMode}
                            notes={agendaNotes[item.id] || ''}
                            onNotesChange={handleNotesChange}
                            onSaveNotes={saveAgendaItemNotes}
                            isSavingNotes={savingNotes[item.id] || false}
                            readOnlyNotes={notesReadOnly}
                            motions={[]}
                            attachments={linkedDocsByAgenda[item.id] || []}
                            onOpenAttachment={handleOpenDocumentById}
                            onAttachDocument={() => setShowDocumentPicker(item.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No agenda items yet</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 bg-gray-100">
                    {docTabs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                        <FileText className="h-12 w-12 mb-3 text-gray-300" />
                        <p>No documents open</p>
                      </div>
                    ) : (
                      (() => {
                        const activeDoc = docTabs.find((doc) => doc.document_id === activeTabId);
                        if (!activeDoc) {
                          return (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                              <FileText className="h-12 w-12 mb-3 text-gray-300" />
                              <p>Select a document tab</p>
                            </div>
                          );
                        }
                        const previewUrl = activeDoc.preview_url || activeDoc.file_url;
                        const pdfPreviewUrl = withPdfZoom(previewUrl);
                        return (
                          <div className="h-full flex flex-col">
                            <div className="bg-white border-b p-3 flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-purple-600" />
                                <span className="font-medium truncate">{activeDoc.title}</span>
                              </div>
                              {activeDoc.file_url && (
                                <a
                                  href={activeDoc.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-purple-600 hover:underline"
                                >
                                  Open in new tab ↗
                                </a>
                              )}
                            </div>
                            <div className="flex-1 bg-gray-800">
                              {previewUrl && (isPdfType(activeDoc.file_type) || activeDoc.preview_url) ? (
                                <iframe
                                  src={pdfPreviewUrl}
                                  className="w-full h-full"
                                  title={activeDoc.title}
                                />
                              ) : activeDoc.file_url && isImageType(activeDoc.file_type) ? (
                                <div className="flex items-center justify-center h-full p-4 bg-white">
                                  <img
                                    src={activeDoc.file_url}
                                    alt={activeDoc.title}
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center h-full text-white">
                                  <FileText className="h-16 w-16 mb-4 opacity-50" />
                                  <p className="text-lg mb-2">Preview not available</p>
                                  {activeDoc.file_url && (
                                    <a
                                      href={activeDoc.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
                                    >
                                      Download Document
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
            </div>
          }
          rightContent={
            <div className="space-y-6">
              {/* Attendees */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Committee Members
                </h3>
                {committeeMembers.length > 0 ? (
                  <div className="space-y-2">
                    {committeeMembers.map((member) => (
                      <div key={member.user_id} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-medium text-purple-600">
                          {member.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name}</p>
                          {member.role && (
                            <p className="text-xs text-gray-500">{member.role}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No members listed</p>
                )}
              </div>

              {/* Documents */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Meeting Documents
                </h3>
                {meetingDocs.length > 0 ? (
                  <div className="space-y-2">
                    {meetingDocs.map((doc) => (
                      <button
                        key={doc.document_id || doc.id}
                        type="button"
                        onClick={() => handleOpenDocumentById(doc.document_id || doc.id)}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors text-left w-full"
                      >
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700 hover:text-purple-600">{doc.title}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No documents linked</p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowAttendance(true)}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Take Attendance
                  </Button>
                  <Button
                    variant={noteTakingMode ? "default" : "outline"}
                    className={`w-full justify-start ${noteTakingMode ? 'bg-purple-600 text-white' : ''}`}
                    onClick={noteTakingMode ? async () => {
                      setNoteTakingMode(false);
                      await releaseNotesLock();
                      setNotesReadOnly(false);
                    } : enableNoteTakingMode}
                  >
                    <PenLine className="h-4 w-4 mr-2" />
                    {noteTakingMode ? 'Exit Quick Notes' : 'Quick Notes'}
                  </Button>
                  {canGenerateFollowupTasks && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={generateFollowupTasks}
                      disabled={generatingFollowupTasks}
                    >
                      {generatingFollowupTasks ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ClipboardList className="h-4 w-4 mr-2" />
                      )}
                      Generate Follow-up Tasks
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleOpenFirstMeetingDoc}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Documents
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowParkingLot(true)}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Parking Lot
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      </div>
      <Dialog open={!!showDocumentPicker} onOpenChange={(open) => !open && setShowDocumentPicker(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attach Document to Agenda Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search documents..."
              value={documentSearch}
              onChange={(e) => setDocumentSearch(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto border rounded-md">
              {documentsLoading ? (
                <div className="p-4 text-sm text-gray-500">Loading documents...</div>
              ) : availableDocuments.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No documents found</div>
              ) : (
                availableDocuments.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                    onClick={() => handleLinkDocument(doc.id, showDocumentPicker)}
                  >
                    <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                    <div className="text-xs text-gray-500">{doc.category || doc.file_type}</div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <TimeAnalyticsPanel
        meetingId={meetingId}
        meeting={{ ...meeting, scheduled_date: meeting?.scheduled_start }}
        agendaItems={agendaItems}
        timing={timing}
        isOpen={showTimeAnalytics}
        onClose={() => setShowTimeAnalytics(false)}
      />
      <AttendancePanel
        open={showAttendance}
        onClose={() => setShowAttendance(false)}
        meetingId={meetingId}
        isChair={true}
        endpointBase={`/governance/committees/${committeeId}`}
        memberLabel="Committee Members"
        allowRollCall={false}
        showQuorum={false}
        title="Attendance"
      />
      <ParkingLotPanel
        meetingId={meetingId}
        agendaItems={agendaItems}
        isOpen={showParkingLot}
        onClose={() => setShowParkingLot(false)}
        isLive={meeting?.status === 'in_progress'}
        endpointBase={`/governance/committees/${committeeId}`}
        allowConvertToAction={false}
      />
    </div>
  );
};

export default CommitteeMeetingRunner;
